import * as React from 'react';
import {
  FormLabel,
  FormLabelProps,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Stack,
  styled,
  TextFieldProps,
} from '@mui/material';
import { isIOSDevice } from 'utils/deviceInfoUtils';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

const MAX_DATE = '9999-12-31';

const StyledStack = styled(Stack)(({ theme }) => ({
  position: 'relative',
  minWidth: '11.25rem',
  '@supports not (-webkit-appearance: none)': {
    // Hide calendar icon for browsers that do not support webkit like Mozilla Firefox
    '&::after': {
      width: '1.5rem',
      height: '1.5rem',
      background: theme.palette.common.white,
      position: 'absolute',
      content: '""',
      right: '1rem',
      top: '2.5rem',
    },
  },
}));

const StyledTextField = styled(MuiTextField)<MuiTextFieldProps>(({ theme, disabled }) => ({
  color: theme.palette.grey[800],
  '.MuiInputBase-input::-webkit-calendar-picker-indicator': {
    display: 'none', // Hide the calendar icon
  },
  // On touch devices, some Android Chrome builds (e.g. Samsung) need the indicator in the layout
  // to open the picker, so keep it invisible and stretch it over the input instead of hiding it.
  // Limited to coarse pointers because the overlay blocks typing into the date segments on desktop.
  '@media (pointer: coarse)': {
    '.MuiInputBase-input::-webkit-calendar-picker-indicator': {
      display: 'block',
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      margin: 0,
      padding: 0,
      opacity: 0,
    },
  },
  '.MuiInputBase-input': {
    textTransform: 'uppercase', // convert the default value DD/MM/YYYY to uppercase
  },
}));

const StyledLabel = styled(FormLabel)<FormLabelProps>(({ theme }) => ({
  color: theme.palette.grey[800],
}));

export const DateField: React.FC<MuiTextFieldProps> = ({
  variant = 'outlined',
  label,
  id,
  ...props
}) => {
  const labelId = `${id}-label`;

  return (
    <StyledStack className="date-field" spacing={1}>
      {label && <StyledLabel id={labelId}>{label}</StyledLabel>}
      <StyledTextField
        className="date-field__input"
        variant={variant}
        type="date"
        slotProps={{
          ...props.slotProps,
          htmlInput: {
            max: MAX_DATE,
            // To fix a bug with iOS WebKit where VoiceOver cannot access the date input,
            // we have to give it a role of textbox (even if this isn't 100% semantically correct)
            // eslint-disable-next-line max-len
            // https://dev.to/mfranzke/voiceover-bug-on-ios-safari-blocks-date-time-related-inputs-especially-in-react-4f61
            // just have this role for ios devices
            ...(isIOSDevice() ? { role: 'textbox' } : {}),
            'aria-labelledby': labelId,
            ...props.slotProps?.htmlInput,
          },
        }}
        {...props}
      />
    </StyledStack>
  );
};

type FormDateFieldProps<T extends FieldValues> = TextFieldProps & {
  control: Control<T>;
  name: Path<T>;
  rules?: any;
};

export const FormDateField = <T extends FieldValues>({
  control,
  name,
  rules,
  ...dateFieldProps
}: FormDateFieldProps<T>) => (
  <Controller
    name={name}
    rules={rules}
    control={control}
    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
      <DateField
        {...dateFieldProps}
        onChange={onChange}
        onBlur={onBlur}
        value={value ?? ''}
        error={!!error}
        helperText={error?.message || dateFieldProps?.helperText}
      />
    )}
  />
);
